import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { toastr } from 'react-redux-toastr';
import i18n from 'i18next';

import Modal from 'common/components/organisms/Modal';
import AlertPrompt from 'common/components/organisms/AlertPrompt';
import InputField from 'common/components/atoms/InputField';
import Error from 'common/typography/Error/Error';
import {
  OverallProgressContainer,
  ProgressContainer,
  ProgressFill,
  ProgressName,
  ProgressRow,
  ProgressTrack,
  ProgressValue,
  StatusBadge
} from 'common/components/molecules/CloneProgress.styled';
import { parseErrorMessage } from 'common/helpers/errors';
import { cloneStatusTheme } from 'common/helpers/cloneStatusTheme';
import {
  isMachineCloneFinalStatus,
  normalizeMachineCloneStatus
} from 'common/constants/machineCloneStatus';
import {
  Button,
  ButtonContainer,
  HintText,
  TimeFilterRow
} from './CloneMachinesModal.styled';

function CloneMachinesModal({
  targetAccountId,
  closeModal,
  fetchSourceSites,
  fetchSourceMachines,
  submitCloneToAccount,
  cloneTaskProgressList,
  onCloneTaskStarted,
  onCloneCompleted,
  theme
}) {
  const [sourceSites, setSourceSites] = useState([]);
  const [sourceMachines, setSourceMachines] = useState([]);
  const [sourceAccountIds, setSourceAccountIds] = useState([]);
  const [selectedMachineIds, setSelectedMachineIds] = useState([]);
  const [startTimestamp, setStartTimestamp] = useState('');
  const [endTimestamp, setEndTimestamp] = useState('');
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isLoadingMachines, setIsLoadingMachines] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showCloneFailurePopup, setShowCloneFailurePopup] = useState(false);
  const [cloneFailureMessage, setCloneFailureMessage] = useState('');
  const [activeTaskId, setActiveTaskId] = useState('');
  const [targetMachineNames, setTargetMachineNames] = useState(new Set());
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setIsLoadingSites(true);
    fetchSourceSites().then(
      (res) => {
        if (!isMounted) return;
        setSourceSites((res && res.items) || []);
      },
      (fetchError) => {
        if (!isMounted) return;
        setError(parseErrorMessage(fetchError));
      }
    ).finally(() => {
      if (isMounted) setIsLoadingSites(false);
    });

    return () => {
      isMounted = false;
    };
  }, [fetchSourceSites]);

  // Load the target site's machines so an already-present name can be flagged before
  // the clone runs. A failure here only costs the warning, so it is not surfaced.
  useEffect(() => {
    let isMounted = true;
    fetchSourceMachines(targetAccountId).then(
      (res) => {
        if (!isMounted) return;
        const names = ((res && res.items) || [])
          .map(machine => String(machine.name || '').trim().toLowerCase())
          .filter(name => !!name);
        setTargetMachineNames(new Set(names));
      },
      () => {}
    );

    return () => {
      isMounted = false;
    };
  }, [fetchSourceMachines, targetAccountId]);

  // The modal is remounted every time it opens, so rehydrate the running task from
  // the persisted list instead of showing a blank form while a clone is in flight.
  useEffect(() => {
    if (activeTaskId) return;
    const runningTask = (cloneTaskProgressList || []).find(task => (
      Number(task.targetAccountId) === Number(targetAccountId)
      && !isMachineCloneFinalStatus(normalizeMachineCloneStatus(task.status))
    ));
    if (runningTask) {
      setActiveTaskId(String(runningTask.taskId));
    }
  }, [cloneTaskProgressList, targetAccountId, activeTaskId]);

  // The target site cannot also be the source, so disable it in the list rather than
  // letting it be picked and only failing validation on submit.
  const siteOptions = useMemo(() => sourceSites
    .map((site) => {
      const isTarget = Number(site.id) === Number(targetAccountId);
      return {
        value: site.id,
        text: isTarget
          ? `${site.name} ${i18n.t('assetHierarchy.cloneMachines.targetSiteSuffix', { defaultValue: '(target site)' })}`
          : site.name,
        disabled: isTarget
      };
    }), [sourceSites, targetAccountId]);

  const machineOptions = useMemo(() => sourceMachines
    .map((machine) => {
      const sourceLabel = machine.source_account_name ? ` (${machine.source_account_name})` : '';
      const duplicateLabel = targetMachineNames.has(String(machine.name).trim().toLowerCase())
        ? `, ${i18n.t('assetHierarchy.cloneMachines.alreadyInTarget', { defaultValue: 'already in target' })}`
        : '';
      return {
        value: machine.id,
        text: `${machine.name}${sourceLabel}${duplicateLabel}`
      };
    }), [sourceMachines, targetMachineNames]);

  // Machines whose name already exists in the target site. The backend decides a
  // duplicate by exactly this rule (Machine.find_by_name_and_account), so matching
  // on name here is the same test, not an approximation.
  const duplicateSelectedMachines = useMemo(
    () => selectedMachineIds
      .map(machineId => sourceMachines.find(item => item.id === machineId))
      .filter(machine => !!machine
        && targetMachineNames.has(String(machine.name).trim().toLowerCase())),
    [selectedMachineIds, sourceMachines, targetMachineNames]
  );

  const isSourceSiteSelected = value => sourceAccountIds.includes(value);
  const isMachineSelected = value => selectedMachineIds.includes(value);

  // There is no data to clone from the future, so cap both pickers at today. Built
  // from local date parts, not toISOString(), which is UTC and would block "today"
  // for anyone east of UTC.
  const today = useMemo(() => {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }, []);

  const sourceSiteSelectionText = useMemo(() => {
    if (!sourceAccountIds.length) {
      return i18n.t('assetHierarchy.cloneMachines.sourceSitePlaceholder', { defaultValue: 'Select source site' });
    }
    return i18n.t('assetHierarchy.cloneMachines.sourceSiteSelected', {
      defaultValue: '{{count}} sites selected',
      count: sourceAccountIds.length
    });
  }, [sourceAccountIds]);

  const machineSelectionText = useMemo(() => {
    if (!selectedMachineIds.length) {
      return i18n.t('assetHierarchy.cloneMachines.machinePlaceholder', { defaultValue: 'Select machines' });
    }
    return i18n.t('assetHierarchy.cloneMachines.machineSelected', {
      defaultValue: '{{count}} selected',
      count: selectedMachineIds.length
    });
  }, [selectedMachineIds]);

  const selectedMachinesProgressRows = useMemo(() => selectedMachineIds.map((machineId) => {
    const machine = sourceMachines.find(item => item.id === machineId);
    return {
      id: machineId,
      name: machine ? machine.name : `${i18n.t('assetHierarchy.cloneMachines.machineLabel', { defaultValue: 'Machine' })} ${machineId}`
    };
  }), [selectedMachineIds, sourceMachines]);

  const activeCloneTask = useMemo(
    () => (cloneTaskProgressList || []).find(task => String(task.taskId) === String(activeTaskId)),
    [cloneTaskProgressList, activeTaskId]
  );

  const activeTaskStatus = normalizeMachineCloneStatus(activeCloneTask && activeCloneTask.status);
  const isTaskRunning = !!activeCloneTask && !isMachineCloneFinalStatus(activeTaskStatus);

  // Rows only exist once a clone task does. Rendering them from the selection made
  // every machine read "Pending" before anything had started.
  const progressRows = activeCloneTask && Array.isArray(activeCloneTask.machineRows)
    ? activeCloneTask.machineRows
    : [];

  const isTaskFinished = !!activeCloneTask && isMachineCloneFinalStatus(activeTaskStatus);

  const completedMachines = Number(activeCloneTask && activeCloneTask.summary && activeCloneTask.summary.completed_machines) || 0;
  const totalMachines = Number(activeCloneTask && activeCloneTask.summary && activeCloneTask.summary.total_machines)
    || selectedMachinesProgressRows.length;
  const progressPercent = totalMachines > 0 ? Math.max(0, Math.min(100, Math.round((completedMachines / totalMachines) * 100))) : 0;

  const toggleSourceSiteSelection = (value, checked) => {
    if (typeof value !== 'number') return;
    setSourceAccountIds((prevIds) => {
      const nextIds = new Set(prevIds);
      if (checked) {
        nextIds.delete(value);
      } else {
        nextIds.add(value);
      }
      return Array.from(nextIds);
    });
    setError('');
  };

  const toggleMachineSelection = (value, checked) => {
    if (typeof value !== 'number') return;
    setSelectedMachineIds((prevIds) => {
      const nextIds = new Set(prevIds);
      if (checked) {
        nextIds.delete(value);
      } else {
        nextIds.add(value);
      }
      return Array.from(nextIds);
    });
    setError('');
  };

  useEffect(() => {
    let isMounted = true;
    setSelectedMachineIds([]);
    setSourceMachines([]);
    if (!sourceAccountIds.length) {
      return () => {
        isMounted = false;
      };
    }

    setIsLoadingMachines(true);
    Promise.all(sourceAccountIds.map(sourceAccountId => fetchSourceMachines(sourceAccountId))).then(
      (responses) => {
        if (!isMounted) return;
        const mergedById = {};
        responses.forEach((res, index) => {
          const currentSourceId = sourceAccountIds[index];
          const sourceSite = sourceSites.find(site => site.id === currentSourceId);
          ((res && res.items) || []).forEach((machine) => {
            mergedById[machine.id] = {
              ...machine,
              source_account_id: currentSourceId,
              source_account_name: sourceSite ? sourceSite.name : ''
            };
          });
        });
        setSourceMachines(Object.values(mergedById));
      },
      (fetchError) => {
        if (!isMounted) return;
        setError(parseErrorMessage(fetchError));
      }
    ).finally(() => {
      if (isMounted) {
        setIsLoadingMachines(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [sourceAccountIds, fetchSourceMachines, sourceSites]);

  const validate = () => {
    if (!sourceAccountIds.length) {
      return i18n.t('assetHierarchy.cloneMachines.validation.sourceRequired', { defaultValue: 'At least one source site is required.' });
    }
    if (sourceAccountIds.some(sourceId => Number(sourceId) === Number(targetAccountId))) {
      return i18n.t('assetHierarchy.cloneMachines.validation.sourceTargetDifferent', { defaultValue: 'Source and target site must be different.' });
    }
    if (!selectedMachineIds.length) {
      return i18n.t('assetHierarchy.cloneMachines.validation.machinesRequired', { defaultValue: 'Select at least one machine.' });
    }
    if (startTimestamp && endTimestamp) {
      const start = new Date(startTimestamp).getTime();
      const end = new Date(endTimestamp).getTime();
      if (Number.isFinite(start) && Number.isFinite(end) && start >= end) {
        return i18n.t('assetHierarchy.cloneMachines.validation.invalidRange', { defaultValue: 'Start date must be before end date.' });
      }
    }
    // Compare against the end of the local day so a date picked today is never
    // treated as future just because the input parses as UTC midnight.
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    const isFuture = value => !!value
      && Number.isFinite(new Date(value).getTime())
      && new Date(value).getTime() > endOfToday.getTime();
    if (isFuture(startTimestamp) || isFuture(endTimestamp)) {
      return i18n.t('assetHierarchy.cloneMachines.validation.futureDate', { defaultValue: 'Dates cannot be in the future.' });
    }
    return '';
  };

  const closeCloneFailurePopup = () => {
    setShowCloneFailurePopup(false);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setShowDuplicateConfirm(false);
    setError('');
    setShowCloneFailurePopup(false);
    setCloneFailureMessage('');
    setIsSubmitting(true);

    const payload = {
      target_account_id: Number(targetAccountId),
      source_account_ids: sourceAccountIds.map(sourceId => Number(sourceId)),
      machine_ids: Array.from(new Set(selectedMachineIds))
    };
    if (sourceAccountIds.length === 1) {
      payload.source_account_id = Number(sourceAccountIds[0]);
    }
    if (startTimestamp) {
      payload.start_timestamp = new Date(startTimestamp).toISOString();
    }
    if (endTimestamp) {
      payload.end_timestamp = new Date(endTimestamp).toISOString();
    }

    try {
      const res = await submitCloneToAccount(payload);
      const taskId = (res && (res.task_id || res.async_task_id));
      const selectedMachines = selectedMachineIds.map((machineId) => {
        const machine = sourceMachines.find(item => item.id === machineId);
        return {
          id: machineId,
          name: machine ? machine.name : `${i18n.t('assetHierarchy.cloneMachines.machineLabel', { defaultValue: 'Machine' })} ${machineId}`,
          sourceAccountName: (machine && machine.source_account_name) || ''
        };
      });

      if (taskId) {
        toastr.success(i18n.t('assetHierarchy.cloneMachines.started', { defaultValue: 'Machine clone started.' }));
        onCloneTaskStarted({
          taskId: String(taskId),
          targetAccountId: Number(targetAccountId),
          selectedMachines,
          sourceSites
        });
        setActiveTaskId(String(taskId));
        setError('');
        return;
      }

      toastr.success(i18n.t('assetHierarchy.cloneMachines.completed', { defaultValue: 'Machine clone completed successfully.' }));
      onCloneCompleted();
      setError('');
    } catch (submitError) {
      const message = parseErrorMessage(submitError);
      setError(message);
      setCloneFailureMessage(message);
      setShowCloneFailurePopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cloning a machine that already exists in the target is allowed, it just lands as
  // "Name_1". Confirm first so the duplicate is deliberate rather than a surprise.
  const handleCloneClick = () => {
    if (isSubmitting) return;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (duplicateSelectedMachines.length) {
      setShowDuplicateConfirm(true);
      return;
    }
    handleSubmit();
  };

  const duplicateConfirmMessage = i18n.t('assetHierarchy.cloneMachines.duplicateWarning', {
    defaultValue: '{{count}} of the {{selected}} selected machines already exist in this site. Cloning again creates a copy named "{{example}}_1". Continue?',
    count: duplicateSelectedMachines.length,
    selected: selectedMachineIds.length,
    example: duplicateSelectedMachines.length ? duplicateSelectedMachines[0].name : ''
  });

  return (
    <>
      {showDuplicateConfirm && (
        <AlertPrompt
          message={i18n.t('assetHierarchy.cloneMachines.duplicateTitle', { defaultValue: 'Some machines are already in this site' })}
          secondaryMessage={duplicateConfirmMessage}
          onProceed={handleSubmit}
          onCancel={() => setShowDuplicateConfirm(false)}
          proceedText={i18n.t('assetHierarchy.cloneMachines.cloneAnyway', { defaultValue: 'CLONE ANYWAY' })}
          cancelText={i18n.t('common.cancelUppercase', { defaultValue: 'CANCEL' })}
          width="480px"
        />
      )}
      {showCloneFailurePopup && (
        <AlertPrompt
          message={i18n.t('assetHierarchy.cloneMachines.failedTitle', { defaultValue: 'Machine cloning failed' })}
          secondaryMessage={cloneFailureMessage}
          onProceed={closeCloneFailurePopup}
          onCancel={closeCloneFailurePopup}
          proceedText={i18n.t('common.closeUppercase', { defaultValue: 'CLOSE' })}
          cancelText={i18n.t('common.dismissUppercase', { defaultValue: 'DISMISS' })}
          proceedColor={theme.colors.cloneStatusFailedText}
          width="480px"
        />
      )}
      <Modal
        title={i18n.t('assetHierarchy.cloneMachines.title', { defaultValue: 'Clone Machines' })}
        close={closeModal}
        width="600px"
        padding="10px 20px 20px 20px"
      >
      <InputField
        label={i18n.t('assetHierarchy.cloneMachines.sourceSite', { defaultValue: 'Source Site' })}
        type="select"
        options={siteOptions}
        marginBottom="6px"
        multiCheckbox
        showSearchInMenu
        searchPlaceholder={i18n.t('assetHierarchy.cloneMachines.searchSourceSites', { defaultValue: 'Type to search sites...' })}
        toggleSelection={toggleSourceSiteSelection}
        isItemSelected={isSourceSiteSelected}
        text={sourceSiteSelectionText}
        placeholder={i18n.t('assetHierarchy.cloneMachines.sourceSitePlaceholder', { defaultValue: 'Select source site' })}
        disabled={isLoadingSites || isSubmitting || isTaskRunning}
      />
      <InputField
        label={i18n.t('assetHierarchy.cloneMachines.machines', { defaultValue: 'Machines' })}
        type="select"
        options={machineOptions}
        marginBottom="6px"
        multiCheckbox
        toggleSelection={toggleMachineSelection}
        isItemSelected={isMachineSelected}
        text={machineSelectionText}
        placeholder={i18n.t('assetHierarchy.cloneMachines.machinePlaceholder', { defaultValue: 'Select machines' })}
        showSearchInMenu
        searchPlaceholder={i18n.t('assetHierarchy.cloneMachines.searchMachines', { defaultValue: 'Type to search machines...' })}
        disabled={!sourceAccountIds.length || isLoadingMachines || isSubmitting || isTaskRunning}
      />
      {isLoadingMachines && (
        <HintText>
          {i18n.t('assetHierarchy.cloneMachines.loadingMachines', { defaultValue: 'Loading machines...' })}
        </HintText>
      )}
      <TimeFilterRow>
        <InputField
          label={i18n.t('assetHierarchy.cloneMachines.startDate', { defaultValue: 'Start Date' })}
          type="date"
          value={startTimestamp}
          max={today}
          onChange={e => setStartTimestamp(e.target.value)}
          disabled={isSubmitting || isTaskRunning}
        />
        <InputField
          label={i18n.t('assetHierarchy.cloneMachines.endDate', { defaultValue: 'End Date' })}
          type="date"
          value={endTimestamp}
          max={today}
          onChange={e => setEndTimestamp(e.target.value)}
          disabled={isSubmitting || isTaskRunning}
        />
      </TimeFilterRow>
      {!!progressRows.length && (
        <OverallProgressContainer direction="column">
          <HintText>
            {i18n.t('assetHierarchy.cloneMachines.overallProgress', {
              defaultValue: '{{completed}} / {{total}} machines cloned',
              completed: completedMachines,
              total: totalMachines
            })}
          </HintText>
          <ProgressTrack>
            <ProgressFill progress={progressPercent} />
          </ProgressTrack>
        </OverallProgressContainer>
      )}
      {!!progressRows.length && (
        <ProgressContainer direction="column">
          {progressRows.map((machine) => {
            const status = normalizeMachineCloneStatus(machine.status);
            const badgeTheme = cloneStatusTheme(status, theme);
            return (
            <ProgressRow key={machine.id}>
              <ProgressName>{machine.name}</ProgressName>
              <ProgressValue>
                <StatusBadge backgroundColor={badgeTheme.backgroundColor} color={badgeTheme.color}>
                  {status.replace('_', ' ')}
                </StatusBadge>
                {machine.new_machine_name ? ` (${machine.new_machine_name})` : ''}
              </ProgressValue>
            </ProgressRow>
            );
          })}
        </ProgressContainer>
      )}

      {!!activeCloneTask && !!activeCloneTask.errorMessage && (
        <Error>{activeCloneTask.errorMessage}</Error>
      )}

      {!!error && (
        <Error>{error}</Error>
      )}

        <ButtonContainer>
          <Button cancel onClick={closeModal} disabled={isSubmitting}>
            {isTaskFinished
              ? i18n.t('common.close', { defaultValue: 'Close' })
              : i18n.t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          {!isTaskFinished && (
            <Button
              onClick={handleCloneClick}
              disabled={
                isSubmitting
                || isTaskRunning
                || !sourceAccountIds.length
                || !selectedMachineIds.length
              }
            >
              {i18n.t('common.clone', { defaultValue: 'Clone' })}
            </Button>
          )}
        </ButtonContainer>
      </Modal>
    </>
  );
}

CloneMachinesModal.propTypes = {
  targetAccountId: PropTypes.number.isRequired,
  closeModal: PropTypes.func.isRequired,
  fetchSourceSites: PropTypes.func.isRequired,
  fetchSourceMachines: PropTypes.func.isRequired,
  submitCloneToAccount: PropTypes.func.isRequired,
  cloneTaskProgressList: PropTypes.arrayOf(PropTypes.shape({
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    errorMessage: PropTypes.string,
    summary: PropTypes.shape({
      completed_machines: PropTypes.number,
      total_machines: PropTypes.number
    }),
    machineRows: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      status: PropTypes.string,
      new_machine_name: PropTypes.string
    }))
  })),
  onCloneTaskStarted: PropTypes.func.isRequired,
  onCloneCompleted: PropTypes.func.isRequired,
  theme: PropTypes.shape({})
};

CloneMachinesModal.defaultProps = {
  cloneTaskProgressList: [],
  theme: {}
};

export default withTheme(CloneMachinesModal);
